import { useState, type FormEvent } from 'react';
import { api } from '../api';
import { Bar, Empty, ErrorBox, Field, Loading, PageHead, Panel, toast } from '../components/ui';
import { dateLabel, errorMessage } from '../format';
import { useApi } from '../hooks';
import { formatMetric } from '../labels';
import type { Goal, GoalInput, MetricInfo, MetricKey } from '../types';

export function goalText(g: Goal): string {
  if (!g.metric_key) return `직접 입력 진행률 ${Math.round(g.manual_progress)}%`;
  const now = formatMetric(g.metric_key, g.current);
  return `${g.metric_label}: 지금 ${now} → 목표 ${formatMetric(g.metric_key, g.target_value)}`;
}

export function GoalRow({ g }: { g: Goal }) {
  return (
    <li className="goal">
      <div className="row-between">
        <b>{g.title}</b>
        <span className="small muted">{g.deadline ? `~ ${dateLabel(g.deadline)}` : ''}</span>
      </div>
      <Bar value={g.progress} tone={g.progress != null && g.progress >= 1 ? 'good' : 'accent'} />
      <div className="small muted">{goalText(g)}</div>
    </li>
  );
}

const toInput = (g: Goal, patch: Partial<GoalInput> = {}): GoalInput => ({
  title: g.title, description: g.description, metric_key: g.metric_key, target_value: g.target_value,
  manual_progress: g.manual_progress, deadline: g.deadline, status: g.status, ...patch,
});

function GoalForm({ metrics, goal, onDone, onCancel }: { metrics: MetricInfo[]; goal: Goal | null; onDone: () => void; onCancel: () => void }) {
  const [title, setTitle] = useState(goal?.title ?? '');
  const [description, setDescription] = useState(goal?.description ?? '');
  const [metricKey, setMetricKey] = useState<MetricKey | ''>(goal?.metric_key ?? '');
  const [target, setTarget] = useState(goal?.target_value != null ? String(goal.target_value) : '');
  const [manual, setManual] = useState(goal?.manual_progress ?? 0);
  const [deadline, setDeadline] = useState(goal?.deadline ?? '');
  const meta = metrics.find((m) => m.key === metricKey);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const body: GoalInput = {
      title, description, metric_key: metricKey || null, target_value: metricKey ? Number(target) : null,
      manual_progress: manual, deadline: deadline || null, status: goal?.status ?? 'active',
    };
    try {
      if (goal) await api.updateGoal(goal.id, body);
      else await api.createGoal(body);
      toast('목표를 저장했습니다');
      onDone();
    } catch (err) {
      toast(errorMessage(err), 'err');
    }
  };

  return (
    <Panel title={goal ? '목표 고치기' : '새 목표'} className="goal-form">
      <form onSubmit={save}>
        <Field label="목표" htmlFor="g-title">
          <input id="g-title" className="input" value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} placeholder="예: 3개월 안에 판단 보정 개선" />
        </Field>
        <div className="grid-2">
          <Field label="연결할 지표" htmlFor="g-metric" hint="연결하면 만든 시점의 값이 출발점이 됩니다">
            <select id="g-metric" className="input" value={metricKey} onChange={(e) => setMetricKey(e.target.value as MetricKey | '')}>
              <option value="">없음 (진행률 직접 입력)</option>
              {metrics.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          </Field>
          {metricKey ? (
            <Field label={`목표값${meta?.unit ? ` (${meta.unit})` : ''}`} htmlFor="g-target" hint={meta ? `지금 ${formatMetric(meta.key, meta.current)}` : undefined}>
              <input id="g-target" className="input" type="number" step="any" value={target} onChange={(e) => setTarget(e.target.value)} required />
            </Field>
          ) : (
            <Field label={`진행률 ${manual}%`} htmlFor="g-manual">
              <input id="g-manual" type="range" min={0} max={100} value={manual} onChange={(e) => setManual(Number(e.target.value))} />
            </Field>
          )}
        </div>
        <div className="grid-2">
          <Field label="기한" htmlFor="g-deadline">
            <input id="g-deadline" className="input" type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
          </Field>
          <Field label="설명" htmlFor="g-desc">
            <input id="g-desc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
        </div>
        <div className="form-actions">
          <button type="button" className="btn" onClick={onCancel}>취소</button>
          <button type="submit" className="btn btn-primary">저장</button>
        </div>
      </form>
    </Panel>
  );
}

export function Goals() {
  const goals = useApi(api.goals);
  const metrics = useApi(api.metrics);
  const [form, setForm] = useState<{ goal: Goal | null } | null>(null);

  const setStatus = async (g: Goal, status: Goal['status']) => {
    try {
      await api.updateGoal(g.id, toInput(g, { status }));
      goals.reload();
    } catch (e) {
      toast(errorMessage(e), 'err');
    }
  };
  const remove = async (g: Goal) => {
    if (!window.confirm(`목표 「${g.title}」을(를) 지울까요?`)) return;
    try {
      await api.deleteGoal(g.id);
      goals.reload();
    } catch (e) {
      toast(errorMessage(e), 'err');
    }
  };

  return (
    <>
      <PageHead
        title="장기 목표"
        sub="지표에 연결하면 진행률이 자동으로 계산됩니다. 결과(수익·성과)보다 통제할 수 있는 과정 지표를 권합니다."
        actions={<button type="button" className="btn btn-primary" onClick={() => setForm({ goal: null })}>새 목표</button>}
      />
      {form && metrics.data && (
        <GoalForm
          key={form.goal?.id ?? 'new'}
          metrics={metrics.data}
          goal={form.goal}
          onCancel={() => setForm(null)}
          onDone={() => {
            setForm(null);
            goals.reload();
            metrics.reload();
          }}
        />
      )}
      {goals.error ? (
        <ErrorBox message={goals.error} onRetry={goals.reload} />
      ) : !goals.data ? (
        <Loading />
      ) : goals.data.length === 0 ? (
        <Panel><Empty>목표가 없습니다. 예: "30일 기억 유지율 90%", "결과 나온 예측 50건", "지식 100개".</Empty></Panel>
      ) : (
        <ul className="item-list">
          {goals.data.map((g) => (
            <li key={g.id} className={`panel goal-item ${g.status !== 'active' ? 'inactive' : ''}`}>
              <ul className="goal-list"><GoalRow g={g} /></ul>
              {g.description && <p className="small muted">{g.description}</p>}
              <div className="row-gap">
                {g.status !== 'active' && <span className="badge">{g.status === 'done' ? '완료' : '그만둠'}</span>}
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setForm({ goal: g })}>고치기</button>
                {g.status === 'active' ? (
                  <>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => void setStatus(g, 'done')}>완료</button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => void setStatus(g, 'dropped')}>그만두기</button>
                  </>
                ) : (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => void setStatus(g, 'active')}>다시 진행</button>
                )}
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => void remove(g)}>지우기</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
